import React, { useState, useEffect } from "react";
import { Modal, Row, Col } from "react-bootstrap";
import { Button, Input } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useLoader } from "../../_helper/LoaderContext";
import { createSupplierAPI, getCityMaster, getDistrictMaster, getPinCodeMaster, getStateMaster, updateSupplierAPI } from "./SupplierService";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import Select from 'react-select';

const SupplierModal = ({ show, onHide, clinicId, onSaved, initial = null, isEdit = false }) => {
    const { t } = useTranslation();
    const { setLoading } = useLoader();

    const [currentSupplier, setCurrentSupplier] = useState(
        initial || {
            companyName: "",
            name: "",
            email: "",
            phone: "",
            phoneCode: "",
            address: "",
            country: "",
            state: "",
            stateCode: "",
            district: "",
            city: "",
            pincode: "",
            gstNumber: "",
            panNumber: ""
        }
    );

    const [formErrors, setFormErrors] = useState({});

    // Location data states
    const [countriesList] = useState([{ value: 'India', label: 'India' }]);
    const [selectedCountry, setSelectedCountry] = useState(
        { value: 'India', label: 'India' }
    );
    const [statesList, setStatesList] = useState([]);
    const [selectedState, setSelectedState] = useState(null);
    const [districtsList, setDistrictsList] = useState([]);
    const [selectedDistrict, setSelectedDistrict] = useState(null);
    const [citiesList, setCitiesList] = useState([]);
    const [selectedCity, setSelectedCity] = useState(null);

    // Load states on mount and when country changes
    useEffect(() => {
        const loadStates = async () => {
            if (!selectedCountry?.value) return;

            try {
                setLoading(true);
                const response = await getStateMaster({ country: selectedCountry.value });
                if (response.data?.data) {
                    const states = response.data.data.map(state => ({
                        value: state.State,
                        label: state.State,
                        gstCode: state.gstCode
                    }));
                    setStatesList(states);
                }
            } catch (error) {
                console.error("Error loading states:", error);
                toast.warning(t("Failed to load states"));
            } finally {
                setLoading(false);
            }
        };

        loadStates();
    }, [selectedCountry]);

    // Load districts when state changes
    useEffect(() => {
        const loadDistricts = async () => {
            if (!selectedCountry?.value || !selectedState?.value) return;

            try {
                setLoading(true);
                const response = await getDistrictMaster({
                    country: selectedCountry.value,
                    state: selectedState.value
                });

                if (response.data?.data) {
                    const districts = response.data.data.map(district => ({
                        value: district.District,
                        label: district.District
                    }));
                    setDistrictsList(districts);
                }
            } catch (error) {
                console.error("Error loading districts:", error);
                toast.warning(t("Failed to load districts"));
            } finally {
                setLoading(false);
            }
        };

        loadDistricts();
    }, [selectedState]);

    // Load cities when district changes
    useEffect(() => {
        const loadCities = async () => {
            if (!selectedCountry?.value || !selectedState?.value || !selectedDistrict?.value) return;

            try {
                setLoading(true);
                const response = await getCityMaster({
                    country: selectedCountry.value,
                    state: selectedState.value,
                    district: selectedDistrict.value
                });

                if (response.data?.data) {
                    const cities = response.data.data.map(city => ({
                        value: city.City,
                        label: city.City
                    }));
                    setCitiesList(cities);
                }
            } catch (error) {
                console.error("Error loading cities:", error);
                toast.warning(t("Failed to load cities"));
            } finally {
                setLoading(false);
            }
        };

        loadCities();
    }, [selectedDistrict]);

    // Initialize form when modal opens or initial data changes
    useEffect(() => {
        if (show && initial) {
            // Pre-fill form for edit mode
            setCurrentSupplier({
                companyName: initial.companyName || "",
                name: initial.contactPerson || initial.name || "",
                email: initial.email || "",
                phone: initial.phone || "",
                phoneCode: initial.countryCode || initial.phoneCode || "",
                address: initial.address || "",
                country: initial.country || "India",
                state: initial.state || "",
                stateCode: initial.stateCode || "",
                district: initial.district || "",
                city: initial.city || "",
                pincode: initial.pincode || "",
                gstNumber: initial.gstNumber || "",
                panNumber: initial.panNumber || ""
            });

            // Set country
            setSelectedCountry({ value: initial.country || "India", label: initial.country || "India" });

            // Set state if available
            if (initial.state) {
                setSelectedState({ value: initial.state, label: initial.state });
            }

            // Set district if available
            if (initial.district) {
                setSelectedDistrict({ value: initial.district, label: initial.district });
            }

            // Set city if available
            if (initial.city) {
                setSelectedCity({ value: initial.city, label: initial.city });
            }

        } else if (show) {
            // Reset form for add mode
            resetForm();
        }
    }, [show, initial]);

    const resetForm = () => {
        setCurrentSupplier({
            companyName: "",
            name: "",
            email: "",
            phone: "",
            phoneCode: "",
            address: "",
            country: "India",  // Add this line
            state: "",
            stateCode: "",
            district: "",
            city: "",
            pincode: "",
            gstNumber: "",
            panNumber: ""
        });
        setSelectedCountry({ value: 'India', label: 'India' });
        setSelectedState(null);
        setSelectedDistrict(null);
        setSelectedCity(null);
        setStatesList([]);
        setDistrictsList([]);
        setCitiesList([]);
        setFormErrors({});
    };

    const handleCountryChange = (selectedOption) => {
        setSelectedCountry(selectedOption);
        setSelectedState(null);
        setSelectedDistrict(null);
        setSelectedCity(null);
        setDistrictsList([]);
        setCitiesList([]);

        setCurrentSupplier(prev => ({
            ...prev,
            country: selectedOption?.label || "",
            state: "",
            stateCode: "",
            district: "",
            city: ""
        }));
    };

    const handleStateChange = async (selectedOption) => {
        setSelectedState(selectedOption);
        setSelectedDistrict(null);
        setSelectedCity(null);
        setCitiesList([]);
        setCurrentSupplier(prev => ({
            ...prev,
            state: selectedOption?.label || "",
            stateCode: selectedOption.gstCode,
            district: "",
            city: ""
        }));
    };

    const handleDistrictChange = (selectedOption) => {
        setSelectedDistrict(selectedOption);
        setSelectedCity(null);

        setCurrentSupplier(prev => ({
            ...prev,
            district: selectedOption?.label || "",
            city: ""
        }));
    };

    const handleCityChange = (selectedOption) => {
        setSelectedCity(selectedOption);
        setCurrentSupplier(prev => ({
            ...prev,
            city: selectedOption?.label || ""
        }));
    };

    // Function to fetch pincode details
    const fetchPincodeDetails = async (pincode) => {
        if (!pincode || pincode.length !== 6) return;

        try {
            setLoading(true);
            const response = await getPinCodeMaster({ pincode });
            const data = response.data?.data;

            if (data) {
                // Update form with pincode details
                setCurrentSupplier(prev => ({
                    ...prev,
                    state: data.state || prev.state,
                    district: data.district || prev.district,
                    city: data.city || prev.city
                }));

                // Update dropdown selections
                if (data.state) {
                    setSelectedState({ value: data.state, label: data.state });
                }
                if (data.district) {
                    setSelectedDistrict({ value: data.district, label: data.district });
                }
                if (data.city) {
                    setSelectedCity({ value: data.city, label: data.city });
                }
            }
        } catch (error) {
            console.error("Error fetching pincode details:", error);
            // Don't show error as it might just be invalid pincode
        } finally {
            setLoading(false);
        }
    };

    const validateSupplierForm = () => {
        const errors = {};

        // Required fields
        if (!currentSupplier.companyName?.trim()) errors.companyName = t("Company Name is required");
        // if (!currentSupplier.name?.trim()) errors.name = t("Contact Person is required");
        // if (!currentSupplier.email?.trim()) errors.email = t("Email is required");

        // Email format validation
        // if (currentSupplier.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentSupplier.email)) {
        //     errors.email = t("Invalid email address");
        // }

        // if (!currentSupplier.phone?.trim()) errors.phone = t("Phone is required");
        // if (!currentSupplier.address?.trim()) errors.address = t("Address is required");
        // if (!currentSupplier.country?.trim()) errors.country = t("Country is required");
        // if (!currentSupplier.state?.trim()) errors.state = t("State is required");
        // if (!currentSupplier.district?.trim()) errors.district = t("District is required");
        // if (!currentSupplier.city?.trim()) errors.city = t("City is required");
        // if (!currentSupplier.pincode?.trim()) errors.pincode = t("Pincode is required");

        // Pincode validation
        // if (currentSupplier.pincode && !/^\d{6}$/.test(currentSupplier.pincode)) {
        //     errors.pincode = t("Invalid pincode (must be 6 digits)");
        // }

        // GST validation (if provided)
        // if (currentSupplier.gstNumber && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(currentSupplier.gstNumber.toUpperCase())) {
        //     errors.gstNumber = t("Invalid GST number format");
        // }

        // PAN validation (if provided)
        // if (currentSupplier.panNumber && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(currentSupplier.panNumber.toUpperCase())) {
        //     errors.panNumber = t("Invalid PAN number format");
        // }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSaveSupplier = async () => {
        if (!validateSupplierForm()) return;

        const payload = {
            clinicId,
            companyName: currentSupplier.companyName,
            contactPerson: currentSupplier.name,
            email: currentSupplier.email,
            countryCode: currentSupplier.phoneCode,
            phone: currentSupplier.phone,
            address: currentSupplier.address,
            country: currentSupplier.country,
            state: currentSupplier.state,
            stateCode: currentSupplier.stateCode,
            district: currentSupplier.district,
            city: currentSupplier.city,
            pincode: currentSupplier.pincode,
            gstNumber: currentSupplier.gstNumber || "",
            panNumber: currentSupplier.panNumber || ""
        };
        try {
            setLoading(true);
            if (isEdit && initial && initial._id) {
                payload.supplierId = initial._id;
                const response = await updateSupplierAPI(payload);
                if (response.data.status === 'success') {
                    toast.success(t(response.data.message));
                    const updated = response.data?.data?.supplier || response.data?.data || null;
                    if (onSaved) onSaved(updated);
                    handleOnHide();
                } else {
                    toast.warning(response?.data?.message || "Failed to update supplier");
                }
            } else {
                const response = await createSupplierAPI(payload);
                if (response?.data?.status === "success") {
                    toast.success(t(response.data.message || "Supplier created"));
                    const created = response.data?.data?.supplier || response.data?.data || null;
                    if (onSaved) onSaved(created);
                    handleOnHide();
                } else {
                    toast.warning(response?.data?.message || "Failed to create supplier");
                }
            }
        } catch (err) {
            console.error("Create supplier error", err);
            toast.warning(t("Operation failed"));
        } finally {
            setLoading(false);
        }
    };

    const handleOnHide = () => {
        resetForm();
        onHide();
    };

    return (
        <Modal show={!!show} onHide={handleOnHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>{initial ? t("Edit Supplier") : t("Add Supplier")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <Col md={6}>
                        <div className="mb-3">
                            <label className="form-label">{t("Company Name")} <span className="text-danger">*</span></label>
                            <Input
                                type="text"
                                value={currentSupplier.companyName}
                                onChange={(e) => setCurrentSupplier(prev => ({ ...prev, companyName: e.target.value }))}
                                invalid={!!formErrors.companyName}
                                placeholder={t("Enter company name")}
                            />
                            {formErrors.companyName && <div className="text-danger mt-1 small">{formErrors.companyName}</div>}
                        </div>
                    </Col>
                    <Col md={6}>
                        <div className="mb-3">
                            <label className="form-label">{t("Contact Person")}
                                {/* <span className="text-danger">*</span> */}
                            </label>
                            <Input
                                type="text"
                                value={currentSupplier.name}
                                onChange={(e) => setCurrentSupplier(prev => ({ ...prev, name: e.target.value }))}
                                invalid={!!formErrors.name}
                                placeholder={t("Enter contact person name")}
                            />
                            {formErrors.name && <div className="text-danger mt-1 small">{formErrors.name}</div>}
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <div className="mb-3">
                            <label className="form-label">{t("Email")}
                                {/* <span className="text-danger">*</span> */}
                            </label>
                            <Input
                                type="email"
                                value={currentSupplier.email}
                                onChange={(e) => setCurrentSupplier(prev => ({ ...prev, email: e.target.value }))}
                                invalid={!!formErrors.email}
                                placeholder={t("Enter email address")}
                            />
                            {formErrors.email && <div className="text-danger mt-1 small">{formErrors.email}</div>}
                        </div>
                    </Col>
                    <Col md={6}>
                        <div className="mb-3">
                            <label className="form-label">{t("Phone")}
                                {/* <span className="text-danger">*</span> */}
                            </label>
                            <PhoneInput
                                country="in"
                                value={`${currentSupplier.phoneCode}${currentSupplier.phone}`}
                                onChange={(value, country) => {
                                    setCurrentSupplier({
                                        ...currentSupplier,
                                        phoneCode: `+${country.dialCode}`,
                                        phone: value.replace(country.dialCode, ""),
                                    });
                                }}
                                inputClass="form-control"
                                containerClass="form-control p-0"
                            />
                            {formErrors.phone && <div className="text-danger mt-1 small">{formErrors.phone}</div>}
                        </div>
                    </Col>
                </Row>

                <div className="mb-3">
                    <label className="form-label">{t("Address")}
                        {/* <span className="text-danger">*</span> */}
                    </label>
                    <Input
                        type="textarea"
                        value={currentSupplier.address}
                        onChange={(e) => setCurrentSupplier(prev => ({ ...prev, address: e.target.value }))}
                        invalid={!!formErrors.address}
                        placeholder={t("Enter complete address")}
                        rows="2"
                    />
                    {formErrors.address && <div className="text-danger mt-1 small">{formErrors.address}</div>}
                </div>

                <Row>
                    <Col md={6}>
                        <div className="mb-3">
                            <label className="form-label">{t("Country")}
                                {/* <span className="text-danger">*</span> */}
                            </label>
                            <Select
                                options={countriesList}
                                value={selectedCountry}
                                onChange={handleCountryChange}
                                placeholder={t("Select country")}
                                isClearable
                                isSearchable
                                isDisabled
                            />
                            {formErrors.country && <div className="text-danger mt-1 small">{formErrors.country}</div>}
                        </div>
                    </Col>
                    <Col md={6}>
                        <div className="mb-3">
                            <label className="form-label">{t("State")}
                                {/* <span className="text-danger">*</span> */}
                            </label>
                            <Select
                                options={statesList}
                                value={selectedState}
                                onChange={handleStateChange}
                                placeholder={t("Select state")}
                                isClearable
                                isSearchable
                                isDisabled={!selectedCountry}
                            />
                            {formErrors.state && <div className="text-danger mt-1 small">{formErrors.state}</div>}
                        </div>
                    </Col>
                </Row>

                <Row>
                    {/* <Col md={6}>
                        <div className="mb-3">
                            <label className="form-label">{t("District")} <span className="text-danger">*</span></label>
                            <Select
                                options={districtsList}
                                value={selectedDistrict}
                                onChange={handleDistrictChange}
                                placeholder={t("Select district")}
                                isClearable
                                isSearchable
                                isDisabled={!selectedState}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                            {formErrors.district && <div className="text-danger mt-1 small">{formErrors.district}</div>}
                        </div>
                    </Col> */}
                    {/* <Col md={6}>
                        <div className="mb-3">
                            <label className="form-label">{t("City")} <span className="text-danger">*</span></label>
                            <Select
                                options={citiesList}
                                value={selectedCity}
                                onChange={handleCityChange}
                                placeholder={t("Select city")}
                                isClearable
                                isSearchable
                                isDisabled={!selectedDistrict}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                            {formErrors.city && <div className="text-danger mt-1 small">{formErrors.city}</div>}
                        </div>
                    </Col> */}
                    {/* <Col md={6}>
                        <div className="mb-3">
                            <label className="form-label">{t("Pincode")} <span className="text-danger">*</span></label>
                            <Input
                                type="text"
                                value={currentSupplier.pincode}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                    setCurrentSupplier(prev => ({ ...prev, pincode: value }));

                                    // Auto-fetch pincode details when 6 digits entered
                                    if (value.length === 6) {
                                        fetchPincodeDetails(value);
                                    }
                                }}
                                invalid={!!formErrors.pincode}
                                placeholder={t("Enter 6-digit pincode")}
                            />
                            {formErrors.pincode && <div className="text-danger mt-1 small">{formErrors.pincode}</div>}
                        </div>
                    </Col> */}
                    <Col md={6}>
                        <div className="mb-3">
                            <label className="form-label">{t("State Code")}</label>
                            <Input
                                type="text"
                                value={currentSupplier.stateCode || ""}
                                readOnly
                                placeholder={t("Auto-filled from state selection")}
                            />
                        </div>
                    </Col>
                    <Col md={6}>
                        <div className="mb-3">
                            <label className="form-label">{t("GST Number")}</label>
                            <Input
                                type="text"
                                value={currentSupplier.gstNumber}
                                onChange={(e) => setCurrentSupplier(prev => ({
                                    ...prev,
                                    gstNumber: e.target.value.toUpperCase()
                                }))}
                                // invalid={!!formErrors.gstNumber}
                                placeholder={t("Enter GST number")}
                                style={{ textTransform: "uppercase" }}
                            />
                            {/* {formErrors.gstNumber && <div className="text-danger mt-1 small">{formErrors.gstNumber}</div>} */}
                        </div>
                    </Col>
                </Row>

                {/* <Row>
                    <Col md={6}>
                        <div className="mb-3">
                            <label className="form-label">{t("GST Number")}</label>
                            <Input
                                type="text"
                                value={currentSupplier.gstNumber}
                                onChange={(e) => setCurrentSupplier(prev => ({
                                    ...prev,
                                    gstNumber: e.target.value.toUpperCase()
                                }))}
                                invalid={!!formErrors.gstNumber}
                                placeholder={t("Enter GST number")}
                                style={{ textTransform: "uppercase" }}
                            />
                            {formErrors.gstNumber && <div className="text-danger mt-1 small">{formErrors.gstNumber}</div>}
                        </div>
                    </Col>
                    <Col md={6}>
                        <div className="mb-3">
                            <label className="form-label">{t("PAN Number")}</label>
                            <Input
                                type="text"
                                value={currentSupplier.panNumber}
                                onChange={(e) => setCurrentSupplier(prev => ({
                                    ...prev,
                                    panNumber: e.target.value.toUpperCase()
                                }))}
                                invalid={!!formErrors.panNumber}
                                placeholder={t("Enter PAN number")}
                                style={{ textTransform: "uppercase" }}
                            />
                            {formErrors.panNumber && <div className="text-danger mt-1 small">{formErrors.panNumber}</div>}
                        </div>
                    </Col>
                </Row> */}
            </Modal.Body>
            <Modal.Footer>
                <Button color="primary" onClick={handleSaveSupplier}>
                    {isEdit ? t("Update") : t("Submit")}
                </Button>
                <Button color="secondary" onClick={handleOnHide}>
                    {t("Cancel")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SupplierModal;
